import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

const VIEW_STORAGE_PREFIX = 'ybitStudentView:';
const CURRENT_VIEW_KEY = 'ybitCurrentStudentView';
const VIEW_TTL_MS = 8 * 60 * 60 * 1000;

const safeParseJson = (value) => {
  try {
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const getStorage = (storageType) => {
  try {
    if (typeof window === 'undefined') return null;
    return storageType === 'session' ? window.sessionStorage : window.localStorage;
  } catch {
    return null;
  }
};

const createViewId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export const getStudentSessionRollNumber = () => {
  const sessionStorage = getStorage('session');
  const session = safeParseJson(sessionStorage?.getItem('studentSession'));
  return session?.rollNumber || '';
};

export const createStudentViewPath = (basePath, rollNumber, search = '') => {
  if (!rollNumber) return `${basePath}${search || ''}`;

  const viewId = createViewId();
  const payload = JSON.stringify({
    rollNumber,
    createdAt: Date.now()
  });

  const localStorage = getStorage('local');
  const sessionStorage = getStorage('session');
  const storageKey = `${VIEW_STORAGE_PREFIX}${viewId}`;

  let saved = false;
  try {
    if (localStorage) {
      localStorage.setItem(storageKey, payload);
      saved = true;
    }
  } catch {}

  if (!saved) {
    try {
      sessionStorage?.setItem(storageKey, payload);
    } catch {}
  }

  return `${basePath}/view/${viewId}${search || ''}`;
};

export const resolveStudentRollNumber = (params = {}) => {
  const sessionStorage = getStorage('session');

  if (params.viewId) {
    const storageKey = `${VIEW_STORAGE_PREFIX}${params.viewId}`;
    const localStorage = getStorage('local');
    const view = safeParseJson(localStorage?.getItem(storageKey)) ||
      safeParseJson(sessionStorage?.getItem(storageKey));

    if (view?.rollNumber && (!view.createdAt || Date.now() - view.createdAt < VIEW_TTL_MS)) {
      try {
        sessionStorage?.setItem(CURRENT_VIEW_KEY, JSON.stringify({
          rollNumber: view.rollNumber,
          createdAt: Date.now()
        }));
      } catch {}
      return view.rollNumber;
    }
  }

  const sessionRollNumber = getStudentSessionRollNumber();
  if (params.rollNumber || sessionRollNumber) {
    return params.rollNumber || sessionRollNumber;
  }

  const currentView = safeParseJson(sessionStorage?.getItem(CURRENT_VIEW_KEY));
  if (currentView?.rollNumber && (!currentView.createdAt || Date.now() - currentView.createdAt < VIEW_TTL_MS)) {
    return currentView.rollNumber;
  }

  return '';
};

export const useHiddenStudentRoute = (basePath) => {
  const { rollNumber: legacyRollNumber, viewId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const rollNumber = useMemo(
    () => resolveStudentRollNumber({ rollNumber: legacyRollNumber, viewId }),
    [legacyRollNumber, viewId]
  );

  useEffect(() => {
    if (legacyRollNumber && rollNumber && basePath) {
      navigate(createStudentViewPath(basePath, rollNumber, location.search), { replace: true });
    }
  }, [basePath, legacyRollNumber, location.search, navigate, rollNumber]);

  return { rollNumber, navigate, params: { rollNumber: legacyRollNumber, viewId } };
};
